import React, {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import analytics from '@sy/app-client/src/ssrAdapter/services/analytics';
import { mixins } from '@sy/component-library';
import ReducedBlogPost from '~/scenes/Blog/scenes/components/ReducedBlogPost';
import { events } from '~/services/tracking';
import type { TrackProperties } from '~/services/tracking/types';
import type { BlogMainCategory } from '~/services/blog/categories';
import {
	categoriesWithTranslations,
	getCategoryUrl,
} from '~/services/blog/categories';
import type { CacheReducedBlogPost } from '~/services/contentful/model/ContentfulBlogPost';
import { useMediaQuery } from '~/utils/hooks';
import { REDUCED_BLOG_POST_IMAGE_SIZES } from './constants';
import {
	BackgroundWrap,
	Content,
	LoadTrigger,
	NoMorePosts,
	PostGrid,
	SelectedCategory,
	Subtitle,
	Title,
	Wrap,
} from './BlogOverview.styles';

interface BlogOverviewProps {
	category?: BlogMainCategory;
	posts: CacheReducedBlogPost[] | null;
}

const { mediaQueries } = mixins;

const INITIAL_LOADED_POSTS_COUNT = 15;
const ROWS_TO_LOAD_COUNT = 5;

const BlogOverview = ({ category, posts = [] }: BlogOverviewProps) => {
	const [loaded, setLoaded] = useState<number>(
		Math.min(posts?.length || 0, INITIAL_LOADED_POSTS_COUNT),
	);
	const [hasMorePosts, setHasMorePosts] = useState<boolean>(
		(posts?.length || 0) > INITIAL_LOADED_POSTS_COUNT,
	);
	const [loadMoreCount, setLoadMoreCount] = useState<number>(0);

	const { t } = useTranslation('blog');

	const categoryTranslationKey = useMemo<string>(() => {
		if (!category) {
			return 'blog.category.all';
		}
		const categoryUrl = getCategoryUrl(category)!;
		return categoriesWithTranslations[categoryUrl]?.translationKey;
	}, [category]);

	const isLarge = useMediaQuery(mediaQueries.large);
	const isMedium = useMediaQuery(mediaQueries.medium);

	const loadCount = useMemo(() => {
		if (isLarge) return 3 * ROWS_TO_LOAD_COUNT;
		if (isMedium) return 2 * ROWS_TO_LOAD_COUNT;
		return ROWS_TO_LOAD_COUNT;
	}, [isLarge, isMedium]);

	const loadTriggerRef = useRef<HTMLDivElement>(null);

	const trackContentClick = useCallback((properties: TrackProperties) => {
		analytics.track(events.CONTENT_CLICKED, properties);
	}, []);

	const trackLoadMore = useCallback((count: number) => {
		analytics.track(events.BLOG_LOAD_MORE, { count });
	}, []);

	const loadMore = useCallback(() => {
		if (!posts || !hasMorePosts) {
			return;
		}

		const newLoadMoreCount = loadMoreCount + 1;
		setLoadMoreCount(newLoadMoreCount);
		trackLoadMore(newLoadMoreCount);

		setLoaded(currentlyLoaded => {
			if (posts && posts.length <= currentlyLoaded + loadCount) {
				setHasMorePosts(false);
			}
			return currentlyLoaded + loadCount;
		});
	}, [
		loadCount,
		loadMoreCount,
		hasMorePosts,
		posts,
		setHasMorePosts,
		trackLoadMore,
	]);

	useEffect(() => {
		if (!loadTriggerRef.current) {
			return () => {};
		}

		const ref = loadTriggerRef.current;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					loadMore();
				}
			},
			{
				root: null,
				threshold: 0.1,
			},
		);

		observer.observe(ref);
		return () => observer.unobserve(ref);
	}, [loadMore]);

	useEffect(() => {
		analytics.page(category || 'All', 'Blog', {
			contentType: 'Blog',
			pageType: 'Category',
			pageCategory: category || 'All',
		});
	}, [category]);

	useEffect(() => {
		setLoaded(Math.min(posts?.length || 0, INITIAL_LOADED_POSTS_COUNT));
		setHasMorePosts((posts?.length || 0) > INITIAL_LOADED_POSTS_COUNT);
		setLoadMoreCount(0);
	}, [posts]);

	return (
		<Fragment>
			<BackgroundWrap>
				<Wrap>
					<SelectedCategory>{t(categoryTranslationKey)}</SelectedCategory>
					<Title>{t('blog.overview.title')}</Title>
					<Subtitle>{t('blog.overview.subtitle')}</Subtitle>
				</Wrap>
			</BackgroundWrap>

			<Content>
				<PostGrid>
					{(posts || []).slice(0, loaded)?.map((post, index) => (
						<ReducedBlogPost
							imageSizes={REDUCED_BLOG_POST_IMAGE_SIZES}
							key={post.id}
							post={post}
							onClick={() =>
								trackContentClick({
									destinationContentName: post.title,
									contentType: 'Blog',
									destinationUrl: post.urlPath,
									location: 'main feed',
									position: index + 1,
								})
							}
						/>
					))}
				</PostGrid>
			</Content>
			{!hasMorePosts && (
				<NoMorePosts>{t('blog.overview.noMorePosts')}</NoMorePosts>
			)}
			<LoadTrigger ref={loadTriggerRef} />
		</Fragment>
	);
};

export default BlogOverview;
